import type { IntlShape } from 'react-intl';
import type { XPostsUsage } from 'fym-shared/src/music24/xPosts';
import type { CalendarPost, SerializedSocialChannel } from './types';
import { xMessages } from './xMessages';

/**
 * The pure rules of the X plan gate and the X month limit (spec 2026-10-22 §6.5, design
 * x-connector a-d). The server decides; these rules let the screens say it first.
 */

/** The code of a 409 for an X post above the month limit of its X account. */
export const M24_X_MONTH_LIMIT = 'M24_X_MONTH_LIMIT';

/** True when the X account sent all the API posts of this month. */
export function isXPostsFull(usage: XPostsUsage | null | undefined): usage is XPostsUsage {
  return usage !== null && usage !== undefined && usage.used >= usage.limit;
}

/** True when a post at `at` counts in the month of `usage` (before the reset). */
export function isInXPostsMonth(usage: XPostsUsage, at: Date): boolean {
  return at.getTime() < new Date(usage.resetsAt).getTime();
}

/**
 * An X channel with a login on a plan without X (after a downgrade). The login, the posts and the
 * numbers stay, but Music24 does not post to X or read it (design b2, case 6).
 */
export function xChannelNeedsPro(
  channel: Pick<SerializedSocialChannel, 'platform' | 'connection'>,
  canUseX: boolean
): boolean {
  return channel.platform === 'x' && !canUseX && channel.connection !== null;
}

/** The X connect is above the plan: the card and the Composer chip show the lock. */
export function xConnectLocked(channel: Pick<SerializedSocialChannel, 'platform'>, canUseX: boolean): boolean {
  return channel.platform === 'x' && !canUseX;
}

/** The day that the X count resets, as day and short month in the user's locale ("1 Nov"). */
export function formatXResetDate(intl: IntlShape, resetsAt: string): string {
  return intl.formatDate(new Date(resetsAt), { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

/**
 * The count of a 409 `M24_X_MONTH_LIMIT` (`customBaseQuery` puts the `cause` on `m24Limit`), or
 * null for every other error. The post stays a draft.
 */
export function xMonthLimitOf(error: unknown): XPostsUsage | null {
  if (typeof error !== 'object' || error === null) {
    return null;
  }
  const cause = (error as { m24Limit?: unknown }).m24Limit;
  if (typeof cause !== 'object' || cause === null) {
    return null;
  }
  const { code, used, limit, resetsAt } = cause as Record<string, unknown>;
  if (code !== M24_X_MONTH_LIMIT) {
    return null;
  }
  return {
    used: typeof used === 'number' ? used : 0,
    limit: typeof limit === 'number' ? limit : 0,
    resetsAt: typeof resetsAt === 'string' ? resetsAt : '',
  };
}

/**
 * The words for a 409 `M24_X_MONTH_LIMIT` next to the action that the server refused: the limit
 * with its reset day, and what the artist can do. Undefined for every other error.
 */
export function xMonthLimitMessage(intl: IntlShape, error: unknown): string | undefined {
  const usage = xMonthLimitOf(error);
  if (usage === null) {
    return undefined;
  }
  const hint = intl.formatMessage(xMessages.draftHint);
  if (usage.resetsAt === '') {
    return hint;
  }
  return `${intl.formatMessage(xMessages.postsFull, { date: formatXResetDate(intl, usage.resetsAt) })} ${hint}`;
}

/** True for an X draft that waits because its X account used the posts of its month (design d3). */
export function isXDraftAtMonthLimit(
  post: Pick<CalendarPost, 'platform' | 'status' | 'scheduledAt'>,
  xPosts: XPostsUsage | null
): boolean {
  return (
    post.platform === 'x' &&
    post.status === 'draft' &&
    isXPostsFull(xPosts) &&
    isInXPostsMonth(xPosts, new Date(post.scheduledAt))
  );
}
